import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import "./ExpenseLineChart.css";

const sampleData = [
  { month: "Jan", actual: 30, budget: 22 },
  { month: "Feb", actual: 115, budget: 18 },
  { month: "Mar", actual: 135, budget: 14 },
  { month: "Apr", actual: 155, budget: 11 },
  { month: "May", actual: 175, budget: 8 },
  { month: "Jun", actual: 200, budget: 5 },
  { month: "Jul", actual: 185, budget: 3 },
  { month: "Aug", actual: 170, budget: 1 },
  { month: "Sep", actual: 160, budget: 0 },
  { month: "Oct", actual: 160, budget: -1 },
];

const months = [
  { month: "Jan", value: "1" },
  { month: "Feb", value: "2" },
  { month: "Mar", value: "3" },
  { month: "Apr", value: "4" },
  { month: "May", value: "5" },
  { month: "Jun", value: "6" },
  { month: "Jul", value: "7" },
  { month: "Aug", value: "8" },
  { month: "Sep", value: "9" },
  { month: "Oct", value: "10" },
  { month: "Nov", value: "11" },
  { month: "Dec", value: "12" },
];

// Helper function to format numbers with K, M, etc.
const formatYAxisTick = (value) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(1)}k`;
  } else if (value >= 1) {
    return `$${value}`;
  } else {
    return `$${value}`;
  }
};

const computeYAxis = (data) => {
  if (!data || data.length === 0) {
    return {
      yDomain: [0, 10],
      yTicks: [0, 2, 4, 6, 8, 10],
    };
  }

  const maxValue = Math.max(
    ...data.map((d) => Math.max(d.actual || 0, d.budget || 0))
  );

  // If everything is 0, show a small range so the axis is visible
  if (maxValue === 0) {
    return {
      yDomain: [0, 10],
      yTicks: [0, 2, 4, 6, 8, 10],
    };
  }

  // Nice tick calculation (about 5–6 ticks from 0 to max)
  const roughStep = maxValue / 5;
  const pow10 = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const normalized = roughStep / pow10;

  let niceNormalized;
  if (normalized <= 1) niceNormalized = 1;
  else if (normalized <= 2) niceNormalized = 2;
  else if (normalized <= 5) niceNormalized = 5;
  else niceNormalized = 10;

  const step = niceNormalized * pow10;
  const maxTick = Math.ceil(maxValue / step) * step;

  const ticks = [];
  for (let v = 0; v <= maxTick; v += step) {
    ticks.push(v);
  }

  return {
    yDomain: [0, maxTick],
    yTicks: ticks,
  };
};

// Custom styles for the time controls (added inline to not affect existing CSS)
const controlContainerStyle = {
  position: "absolute",
  top: "20px",
  right: "20px",
  zIndex: 10,
  backgroundColor: "white",
  padding: "8px 12px",
  borderRadius: "8px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  border: "1px solid #e0e0e0",
  minWidth: "140px",
  textAlign: "center",
};

const controlStyle = {
  border: "none",
  outline: "none",
  fontSize: "14px",
  color: "#102a3d",
  backgroundColor: "transparent",
  cursor: "pointer",
  fontWeight: "500",
  width: "100%",
  textAlign: "center",
  fontFamily: "inherit",
};

// Year-only calendar selector component
const YearCalendarSelector = ({ selectedYear, onYearChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const currentYear = new Date().getFullYear();
  const [years, setYears] = useState([]);

  // Generate years from currentYear-100 to currentYear+10
  useEffect(() => {
    const yearsArray = [];
    for (let year = currentYear - 100; year <= currentYear + 10; year++) {
      yearsArray.push(year);
    }
    setYears(yearsArray);
  }, [currentYear]);

  const handleYearClick = (year) => {
    onYearChange(year.toString());
    setIsOpen(false);
  };

  const toggleCalendar = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div
      style={{
        ...controlContainerStyle,
        minWidth: "100px",
        cursor: "pointer",
        position: "relative",
        top: "-10px",
        right: "-330px",
        width: "30%",
      }}
    >
      <div
        onClick={toggleCalendar}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "4px 8px",
        }}
      >
        <span style={{ color: selectedYear ? "#102a3d" : "#999" }}>
          {selectedYear || "Select Year"}
        </span>
        <span style={{ fontSize: "12px", color: "#102a3d" }}>▼</span>
      </div>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            backgroundColor: "white",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            maxHeight: "200px",
            overflowY: "auto",
            zIndex: 100,
            marginTop: "4px",
          }}
        >
          <div
            style={{
              padding: "8px",
              backgroundColor: "#f8f9fa",
              borderBottom: "1px solid #e0e0e0",
              position: "sticky",
              top: 0,
              zIndex: 1,
            }}
          >
            <input
              type="text"
              placeholder="Search year..."
              style={{
                width: "100%",
                padding: "4px 8px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontSize: "12px",
              }}
              onChange={(e) => {
                const searchTerm = e.target.value;
                const filteredYears = years.filter((year) =>
                  year.toString().includes(searchTerm)
                );
                setYears(filteredYears);
              }}
            />
          </div>

          <div
            style={{
              maxHeight: "150px",
              overflowY: "auto",
            }}
          >
            {years.map((year) => (
              <div
                key={year}
                onClick={() => handleYearClick(year)}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  borderBottom: "1px solid #f0f0f0",
                  backgroundColor:
                    selectedYear === year.toString() ? "#e3f2fd" : "white",
                  color:
                    selectedYear === year.toString() ? "#1976d2" : "#102a3d",
                  fontSize: "14px",
                  fontWeight: selectedYear === year.toString() ? "600" : "400",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#f5f5f5")
                }
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor =
                    selectedYear === year.toString() ? "#e3f2fd" : "white";
                }}
              >
                {year}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Week selector component
const WeekSelector = ({ selectedWeek, onWeekChange }) => {
  const weeks = Array.from({ length: 52 }, (_, i) => i + 1);

  return (
    <div style={controlContainerStyle}>
      <select
        value={selectedWeek}
        onChange={(e) => onWeekChange(e.target.value)}
        style={controlStyle}
      >
        <option value="">Select Week</option>
        {weeks.map((week) => (
          <option key={week} value={week}>
            Week {week}
          </option>
        ))}
      </select>
    </div>
  );
};

// Month selector component
const MonthSelector = ({ selectedMonth, onMonthChange }) => {
  return (
    <div style={controlContainerStyle}>
      <select
        value={selectedMonth}
        onChange={(e) => onMonthChange(e.target.value)}
        style={controlStyle}
      >
        <option value="">Select Month</option>
        {months.map((m) => (
          <option key={m.value} value={m.value}>
            {m.month}
          </option>
        ))}
      </select>
    </div>
  );
};

// Date picker component
const DatePicker = ({ selectedDate, onDateChange }) => {
  const today = new Date().toISOString().split("T")[0];

  return (
    <div style={controlContainerStyle}>
      <input
        type="date"
        value={selectedDate}
        onChange={(e) => onDateChange(e.target.value)}
        style={controlStyle}
        max={today}
      />
    </div>
  );
};

const ExpenseLineChart = ({
  data = sampleData,
  time,
  setFilter,
  selectedYearFromDashboardPage,
}) => {
  console.log("sampleData: ", sampleData);

  const { yDomain, yTicks } = useMemo(() => computeYAxis(data), [data]);

  // State for each time selector with default values
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedWeek, setSelectedWeek] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");

  // Clear other selections when time changes
  useEffect(() => {
    setSelectedDate("");
    setSelectedWeek("");
    setSelectedMonth("");
    setSelectedYear("");
    // Clear parent selection when time changes
    // setFilter({ day: "", week: "", month: "", year: "" });
    setFilter({
      day: "",
      week: "",
      month: "",
      year: selectedYearFromDashboardPage?.toString(),
    });
  }, [time]);

  // Update parent's selectedTwo whenever any selection changes
  useEffect(() => {
    if (selectedDate) {
      setFilter((prev) => ({
        day: selectedDate,
        week: "",
        month: "",
        year: "",
      }));
    }
  }, [selectedDate]);

  useEffect(() => {
    if (selectedWeek) {
      setFilter((prev) => ({
        day: "",
        week: selectedWeek,
        month: "",
        year: "",
      }));
    }
  }, [selectedWeek]);

  useEffect(() => {
    if (selectedMonth) {
      setFilter((prev) => ({
        day: "",
        week: "",
        month: selectedMonth,
        year: "",
      }));
    }
  }, [selectedMonth]);

  useEffect(() => {
    // if (selectedYear) {
    if (selectedYearFromDashboardPage) {
      setFilter((prev) => ({
        day: "",
        week: "",
        month: "",
        // year: selectedYear,
        year: selectedYearFromDashboardPage?.toString(),
      }));
    }
  }, [selectedYearFromDashboardPage]);
  // }, [selectedYear]);

  const lastPoint = data && data.length > 0 ? data[data.length - 1] : {};
  const latestActual = lastPoint.actual ?? 0;
  const latestBudget = lastPoint.budget ?? 0;

  // Render appropriate control based on time prop
  const renderTimeControl = () => {
    if (!time) return null;

    switch (time.toLowerCase()) {
      case "day":
        return (
          <DatePicker
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
          />
        );

      case "week":
        return (
          <WeekSelector
            selectedWeek={selectedWeek}
            onWeekChange={setSelectedWeek}
          />
        );

      case "month":
        return (
          <MonthSelector
            selectedMonth={selectedMonth}
            onMonthChange={setSelectedMonth}
          />
        );

      // case "year":
      //   return (
      //     <YearCalendarSelector
      //       selectedYear={selectedYear}
      //       onYearChange={setSelectedYear}
      //     />
      //   );

      default:
        return null;
    }
  };

  return (
    <div className="chart-main-ln4">
      <div className="chart-card-ln4" style={{ position: "relative" }}>
        {/* Time-based control - only render if time prop is provided */}
        {time && renderTimeControl()}

        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={data}>
            <CartesianGrid
              vertical={false}
              strokeDasharray="6 6"
              stroke="#97a6b5"
            />

            <XAxis
              dataKey="month"
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#102a3d", fontSize: 14 }}
            />

            <YAxis
              domain={yDomain}
              ticks={yTicks}
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#102a3d", fontSize: 14 }}
              tickFormatter={formatYAxisTick} // Added tickFormatter here
            />

            <Tooltip
              cursor={{ stroke: "#e0e0e0", strokeWidth: 1 }}
              formatter={(value, name) => [
                `$${value}`,
                name === "actual" ? "Actual expense" : "Budget expense",
              ]}
              labelFormatter={(label) => label}
              contentStyle={{
                borderRadius: 10,
                border: "none",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                fontSize: 13,
              }}
            />

            <Line
              type="monotone"
              dataKey="actual"
              stroke="#102a3d"
              strokeWidth={3.5}
              dot={{ r: 6, fill: "#102a3d" }}
              activeDot={{ r: 7, stroke: "#102a3d", strokeWidth: 2 }}
            />

            <Line
              type="monotone"
              dataKey="budget"
              stroke="#9a9a9a"
              strokeWidth={3}
              dot={{ r: 6, fill: "#9a9a9a" }}
              activeDot={{ r: 7, stroke: "#9a9a9a", strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>

        <div className="legend-box-ln4">
          <div className="legend-item-ln4">
            <span className="dot-actual-ln4"></span>
            Actual expense
            <b>${latestActual}</b>
          </div>

          <div className="legend-item-ln4">
            <span className="dot-budget-ln4"></span>
            Budget expense
            <b>${latestBudget}</b>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseLineChart;
